/**
 * Display rounding for BPM.
 *
 * BPM ONLY. Loudness, true peak, energy and cue points keep whatever precision
 * they were measured at: LUFS is a calibrated measurement where -8.05 and -8.0
 * are different readings, and flattening them would misreport a standards-based
 * number. Tempo is the one field where the fractional part is detector noise
 * rather than signal.
 *
 * A detector returns 101.983 or 124.02. Neither is meaningfully more precise
 * than 102 and 124, but 106.673 genuinely is somewhere between 106 and 107 and
 * flattening it would invent precision in the other direction.
 *
 * So the rule snaps only what is already all but touching a whole number:
 *
 *   fraction >= 0.8  -> round UP    (within 0.2 of the next whole number)
 *   fraction <= 0.1  -> round DOWN  (already all but on this whole number)
 *   otherwise        -> leave alone
 *
 * 101.983 -> 102, 124.02 -> 124, 99.99 -> 100, 106.673 -> 106.673.
 *
 * This is presentation only. The stored value keeps its full precision, because
 * the beat grid and any future crossfade alignment are computed from it and
 * rounding there would accumulate drift.
 */

/** Distance below the next whole number that still counts as "basically there". */
export const ROUND_UP_WITHIN = 0.2;

/** Distance above a whole number that still counts as "basically there". */
export const ROUND_DOWN_WITHIN = 0.1;

export function snapNumber(value: number): number {
  if (!Number.isFinite(value)) return value;

  const whole = Math.floor(value);
  const fraction = value - whole;

  // Guard the exact boundaries with a tolerance: floating point makes 0.2 land
  // at 0.19999999999998863, which a bare >= would miss.
  const epsilon = 1e-9;
  if (fraction >= 1 - ROUND_UP_WITHIN - epsilon) return whole + 1;
  if (fraction <= ROUND_DOWN_WITHIN + epsilon) return whole;
  return value;
}

/**
 * Format a number for the grid: snapped, and shown without a pointless ".0".
 *
 * `decimals` caps what survives when the value is NOT snapped.
 */
export function formatNumber(value: number, decimals = 3): string {
  const snapped = snapNumber(value);
  if (Number.isInteger(snapped)) return String(snapped);
  return String(Number(snapped.toFixed(decimals)));
}


/**
 * How close a decimal BPM query has to be to count as a match.
 *
 * Derived from the precision the user actually typed, because that is the
 * precision they can see. The grid shows BPM to one decimal, so a stored 100.724
 * appears as "100.7" -- and a fixed hairline tolerance meant typing the number on
 * screen found nothing. Typing one decimal now matches anything that displays as
 * that value; typing three matches far more tightly.
 *
 * `decimals` is how many digits followed the point in the query.
 */
export function bpmMatchTolerance(decimals: number): number {
  // Half a unit of the last place typed: 1 decimal -> 0.05, 2 -> 0.005.
  return 0.5 * 10 ** -Math.max(0, decimals);
}

/** Digits after the decimal point in a number as the user wrote it. */
export function decimalsTyped(raw: number | string): number {
  const text = String(raw);
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : text.length - dot - 1;
}
